import React from 'react';
import {
  Box, Typography, Paper, CircularProgress, useTheme,
  Avatar, Stack, Card, CardContent, alpha,
} from '@mui/material';
import { useRelatorioCores } from '../../pages/relatorios';

export interface FrotaStatItem {
  key: string;
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  filterValue?: string;
}

interface FrotaStatCardProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick?: () => void;
}

export const FrotaStatCard: React.FC<FrotaStatCardProps> = ({
  label, value, color, icon, active, onClick,
}) => (
  <Card
    elevation={active ? 4 : 0}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      height: '100%',
      border: '2px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: active ? alpha(color || '#2196F3', 0.08) : 'background.paper',
      transition: 'all 0.2s ease',
      ...(onClick ? {
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
          borderColor: color || 'primary.main',
        },
      } : {}),
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: color || 'primary.main', lineHeight: 1.2, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(color || '#2196F3', 0.12),
            color: color || 'primary.main',
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

interface FrotaPageLayoutProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconColor?: string;
  actions?: React.ReactNode;
  stats?: FrotaStatItem[];
  activeFilter?: string;
  onFilterChange?: (filterValue: string) => void;
  filters?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}

const FrotaPageLayout: React.FC<FrotaPageLayoutProps> = ({
  title,
  subtitle,
  icon,
  iconColor = '#2196F3',
  actions,
  stats,
  activeFilter = '',
  onFilterChange,
  filters,
  loading = false,
  children,
}) => {
  const theme = useTheme();
  const relatorioCores = useRelatorioCores();

  return (
    <Box sx={{ p: { xs: 1, md: 1 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(relatorioCores.primario, 0.08)} 0%, ${alpha(relatorioCores.secundario, 0.06)} 100%)`,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.12),
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: alpha(iconColor, 0.15),
                color: iconColor,
                boxShadow: `0 4px 14px ${alpha(iconColor, 0.25)}`,
              }}
            >
              {icon}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            </Box>
          </Stack>
          {actions && (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {actions}
            </Stack>
          )}
        </Box>
      </Paper>

      {stats && stats.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: `repeat(${Math.min(stats.length, 3)}, 1fr)`,
              lg: `repeat(${stats.length}, 1fr)`,
            },
            gap: 2,
          }}
        >
          {stats.map((stat) => (
            <FrotaStatCard
              key={stat.key}
              label={stat.label}
              value={stat.value}
              color={stat.color}
              icon={stat.icon}
              active={activeFilter === (stat.filterValue ?? stat.key)}
              onClick={
                onFilterChange && stat.filterValue !== undefined
                  ? () => onFilterChange(stat.filterValue!)
                  : undefined
              }
            />
          ))}
        </Box>
      )}

      {filters && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {filters}
        </Paper>
      )}

      <Paper
        elevation={3}
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {loading ? (
          <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} thickness={4} />
            <Typography color="text.secondary" fontWeight={500}>Carregando...</Typography>
          </Box>
        ) : children}
      </Paper>
    </Box>
  );
};

export default FrotaPageLayout;
